import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { X, Ticket, Mail, Send } from "lucide-react";
import { useTickets } from "@/context/TicketContext";
import { useSubscribers } from "@/context/SubscriberContext";
import { Header } from "@/components/Header";
import { TicketFormModal } from "@/components/TicketFormModal";
import { CloseTicketModal } from "@/components/CloseTicketModal";
import { formatDispatchTime } from "@/utils/formatDateTime";

const ActiveTicketsPage = () => {
  const { subscriberId } = useParams();
  const navigate = useNavigate();
  const { getTicketsForSubscriber, updateTicket, closeTicket } = useTickets();
  const { subscribers } = useSubscribers();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [emailNotification, setEmailNotification] = useState(null);
  
  const subscriber = subscribers.find(s => s.id === parseInt(subscriberId));
  const tickets = getTicketsForSubscriber(parseInt(subscriberId));

  const handleEditClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setShowCloseModal(false);
    setSelectedTicket(null);
  };

  const handleUpdateTicket = (formData) => {
    updateTicket(formData.ticketId, formData);
    handleCloseModal();
  };

  const handleDispatchClick = async (ticket) => {
    const dispatchedAt = new Date().toISOString();
    const result = await updateTicket(ticket.ticketId, { 
      status: 'Dispatched',
      dispatchedAt: dispatchedAt
    });
    if (result?.emailSent) {
      setEmailNotification(`Dispatch email sent to ${result.emailTo}`);
      setTimeout(() => setEmailNotification(null), 5000);
    }
  };

  const handleCloseTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowCloseModal(true);
  };

  const handleConfirmClose = (ticketId, closingComments) => {
    closeTicket(ticketId, closingComments);
    handleCloseModal();
  };
  
  if (!subscriber) {
    return (
      <div className="app-container">
        <div className="device-page-header">
          <h1>Subscriber not found</h1>
          <button className="close-btn" onClick={() => navigate('/')}>
            <X size={24} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="active-tickets-page">
      <Header />

      {/* Tickets Page Content */}
      <main className="main-content">
        {emailNotification && (
          <div className="email-notification" data-testid="email-notification">
            <Mail size={16} />
            <span>{emailNotification}</span>
          </div>
        )}
        <div className="device-page-header">
          <div>
            <h1 className="device-page-title">{subscriber.name} - Active Tickets</h1>
            <p className="device-page-subtitle">{tickets.length} active ticket{tickets.length !== 1 ? 's' : ''}</p>
          </div>
          <button className="close-btn" onClick={() => navigate('/')} data-testid="close-btn">
            <X size={24} />
          </button>
        </div>

        {tickets.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <Ticket size={48} className="empty-state-icon" />
            <h3>No Active Tickets</h3>
            <p>There are no active tickets for this subscriber yet.</p>
            <button className="btn-back" onClick={() => navigate(`/subscriber/${subscriberId}/devices`)}>
              Go to Need Attention
            </button>
          </div>
        ) : (
          <div className="tickets-list" data-testid="tickets-list">
            {tickets.map(ticket => (
              <div key={ticket.ticketId} className="ticket-card" data-testid={`ticket-card-${ticket.ticketId}`}>
                <div className="ticket-card-header">
                  <div className="ticket-id-section">
                    <Ticket size={18} className="ticket-icon" />
                    <span className="ticket-id">{ticket.ticketId}</span>
                    <span className={`ticket-status-badge ${ticket.status.toLowerCase().replace(' ', '-')}`}>{ticket.status}</span>
                    <span className={`ticket-priority-badge ${ticket.priority.toLowerCase()}`}>{ticket.priority}</span>
                  </div>
                  <div className="ticket-header-right">
                    <div className="ticket-actions-header">
                      <button 
                        className="ticket-action-btn edit" 
                        data-testid={`edit-btn-${ticket.ticketId}`}
                        onClick={() => handleEditClick(ticket)}
                      >
                        EDIT
                      </button>
                      <button 
                        className={`ticket-action-btn dispatch ${ticket.status === 'Dispatched' ? 'dispatched' : ''}`}
                        data-testid={`dispatch-btn-${ticket.ticketId}`}
                        onClick={() => handleDispatchClick(ticket)}
                        disabled={ticket.status === 'Dispatched'}
                      >
                        {ticket.status === 'Dispatched' ? 'DISPATCHED' : 'DISPATCH'}
                      </button>
                      <button 
                        className="ticket-action-btn close-ticket" 
                        data-testid={`close-btn-${ticket.ticketId}`}
                        onClick={() => handleCloseTicketClick(ticket)}
                      >
                        CLOSE
                      </button>
                    </div>
                    <span className="ticket-date">Created: {new Date(ticket.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="ticket-card-body">
                  <div className="ticket-info-row">
                    <span className="ticket-label">Device:</span>
                    <span className="ticket-value">{ticket.deviceId}</span>
                    <span className="ticket-label">Type:</span>
                    <span className="ticket-value">{ticket.deviceType}</span>
                    <span className="ticket-label">Issue:</span>
                    <span className="ticket-value issue">{ticket.issueType}</span>
                  </div>
                  <div className="ticket-info-row">
                    <span className="ticket-label">Location:</span>
                    <span className="ticket-value">{ticket.location}</span>
                  </div>
                  <div className="ticket-info-row">
                    <span className="ticket-label">Assigned To:</span>
                    <span className="ticket-value">{ticket.assignedTo || 'Unassigned'}</span>
                    <span className="ticket-label">Due:</span>
                    <span className="ticket-value">{ticket.dueDate || 'Not set'}</span>
                  </div>
                  <div className="ticket-description">
                    <span className="ticket-label">Description:</span>
                    <p>{ticket.description}</p>
                  </div>
                  {ticket.dispatchedAt && (
                    <div className="ticket-dispatched-info" data-testid={`dispatched-info-${ticket.ticketId}`}>
                      <Send size={16} className="dispatch-icon" />
                      <span className="dispatch-label">Dispatched:</span>
                      <span className="dispatch-time">{formatDispatchTime(ticket.dispatchedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Edit Ticket Modal */}
      {showEditModal && selectedTicket && (
        <TicketFormModal 
          editTicket={selectedTicket}
          subscriber={subscriber}
          onClose={handleCloseModal}
          onSubmit={handleUpdateTicket}
        />
      )}

      {/* Close Ticket Modal */}
      {showCloseModal && selectedTicket && (
        <CloseTicketModal 
          ticket={selectedTicket}
          onClose={handleCloseModal}
          onConfirm={handleConfirmClose}
        />
      )}
    </div>
  );
};

export default ActiveTicketsPage;
